import { Link as ReeferLink } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import type { LinkProps } from 'react-router';
import { Link as RemixLink } from 'react-router';

import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';

import styles from './bloomLink.module.css';

export interface BloomLinkProps extends LinkProps {
  children: ReactNode;
  to: string;
  variant?: 'minimal' | 'reefer-underline';
}

/**
 * Renders a Remix Link with useful defaults.
 * Includes a Reefer Link ensures consistent styling.
 * @example Usage w/ Reefer Style:
 * <BloomLink to="/login" variant="reefer-underline">
 * </BloomLink>
 */
export const BloomLink = forwardRef<HTMLAnchorElement, BloomLinkProps>(
  function BloomLink(
    { children, className, reloadDocument, to, variant = 'minimal', ...props },
    ref
  ) {
    const generatePath = useFullDestination();

    return (
      <RemixLink
        ref={ref}
        className={clsx(
          styles['link'],
          {
            [styles['link--minimal']]: variant === 'minimal',
          },
          className
        )}
        to={generatePath(to)}
        reloadDocument={
          reloadDocument ||
          /**
           * Force a full page refresh when linking to the menu home.
           * Otherwise with Single Fetch enabled, Remix makes a request to `/(:basename).data`,
           * which our reverse proxies do not route back to the Remix server.
           */
          to === '/'
        }
        {...props}
      >
        {variant === 'reefer-underline' ? (
          <ReeferLink
            onClick={
              // the onClick is a workaround to ensure that reefer doesn't make this unclickable
              () => ({})
            }
          >
            {children}
          </ReeferLink>
        ) : (
          children
        )}
      </RemixLink>
    );
  }
);
